import type { StoreApi } from 'zustand/vanilla';
import { createStore } from 'zustand/vanilla';

import type { EcsConfig } from './ecsConfig';
import type { Experiment, ExperimentTreatmentType, ExperimentTreatments } from './experiments';
import type { FeatureRollout, FeatureRolloutTreatments } from './featureRollouts';

export type EcsServiceLoadStatus = 'not-started' | 'loading' | 'succeeded' | 'failed';

export interface EcsServiceSnapshot {
  readonly ecsConfig: EcsConfig;
  readonly experimentTreatmentsFromService: Partial<ExperimentTreatments>;
  readonly featureRolloutTreatmentsFromService: Partial<FeatureRolloutTreatments>;
}

export interface EcsSourceSnapshot extends EcsServiceSnapshot {
  readonly experimentTreatmentOverrides: Partial<ExperimentTreatments>;
  readonly featureRolloutTreatmentOverrides: Partial<FeatureRolloutTreatments>;
}

export interface ExperimentsSourceState extends EcsSourceSnapshot {
  readonly serviceLoadStatus: EcsServiceLoadStatus;
  readonly serviceLoadError: Error | undefined;
}

export interface ExperimentsActions {
  readonly applyHydratedSnapshot: (snapshot: EcsSourceSnapshot) => void;
  readonly beginServiceLoad: () => void;
  readonly completeServiceLoad: (snapshot: EcsServiceSnapshot) => void;
  readonly failServiceLoad: (error: Error) => void;
  readonly addExperimentOverride: <E extends Experiment>(experiment: E, treatment: ExperimentTreatmentType<E>) => void;
  readonly removeExperimentOverride: (experiment: Experiment) => void;
  readonly removeAllExperimentOverrides: () => void;
  readonly addFeatureRolloutOverride: (featureRollout: FeatureRollout, isEnabled: boolean) => void;
  readonly removeFeatureRolloutOverride: (featureRollout: FeatureRollout) => void;
  readonly removeAllFeatureRolloutOverrides: () => void;
}

export type ExperimentsState = ExperimentsSourceState & ExperimentsActions;

export const createInitialEcsSourceSnapshot = (): EcsSourceSnapshot => ({
  ecsConfig: {
    configIds: {},
    etag: '',
  },
  experimentTreatmentsFromService: {},
  featureRolloutTreatmentsFromService: {},
  experimentTreatmentOverrides: {},
  featureRolloutTreatmentOverrides: {},
});

const createInitialExperimentsSourceState = (): ExperimentsSourceState => ({
  ...createInitialEcsSourceSnapshot(),
  serviceLoadStatus: 'not-started',
  serviceLoadError: undefined,
});

const omitExperimentOverride = (
  experimentTreatmentOverrides: Partial<ExperimentTreatments>,
  experiment: Experiment
): Partial<ExperimentTreatments> => {
  const { [experiment]: _omittedTreatment, ...remainingOverrides } = experimentTreatmentOverrides;

  return remainingOverrides;
};

const omitFeatureRolloutOverride = (
  featureRolloutTreatmentOverrides: Partial<FeatureRolloutTreatments>,
  featureRollout: FeatureRollout
): Partial<FeatureRolloutTreatments> => {
  const { [featureRollout]: _omittedTreatment, ...remainingOverrides } = featureRolloutTreatmentOverrides;

  return remainingOverrides;
};

export const createExperimentsStore = (
  initialState: Partial<ExperimentsSourceState> = {}
): StoreApi<ExperimentsState> =>
  createStore<ExperimentsState>((set) => ({
    ...createInitialExperimentsSourceState(),
    ...initialState,
    applyHydratedSnapshot: (snapshot) => {
      set((state) =>
        state.serviceLoadStatus === 'succeeded'
          ? {
              experimentTreatmentOverrides: snapshot.experimentTreatmentOverrides,
              featureRolloutTreatmentOverrides: snapshot.featureRolloutTreatmentOverrides,
            }
          : snapshot
      );
    },
    beginServiceLoad: () => {
      set({
        serviceLoadError: undefined,
        serviceLoadStatus: 'loading',
      });
    },
    completeServiceLoad: (snapshot) => {
      set({
        ...snapshot,
        serviceLoadError: undefined,
        serviceLoadStatus: 'succeeded',
      });
    },
    failServiceLoad: (error) => {
      set({
        serviceLoadError: error,
        serviceLoadStatus: 'failed',
      });
    },
    addExperimentOverride: (experiment, treatment) => {
      set((state) => ({
        experimentTreatmentOverrides: {
          ...state.experimentTreatmentOverrides,
          [experiment]: treatment,
        },
      }));
    },
    removeExperimentOverride: (experiment) => {
      set((state) => ({
        experimentTreatmentOverrides: omitExperimentOverride(state.experimentTreatmentOverrides, experiment),
      }));
    },
    removeAllExperimentOverrides: () => {
      set({
        experimentTreatmentOverrides: {},
      });
    },
    addFeatureRolloutOverride: (featureRollout, isEnabled) => {
      set((state) => ({
        featureRolloutTreatmentOverrides: {
          ...state.featureRolloutTreatmentOverrides,
          [featureRollout]: isEnabled,
        },
      }));
    },
    removeFeatureRolloutOverride: (featureRollout) => {
      set((state) => ({
        featureRolloutTreatmentOverrides: omitFeatureRolloutOverride(
          state.featureRolloutTreatmentOverrides,
          featureRollout
        ),
      }));
    },
    removeAllFeatureRolloutOverrides: () => {
      set({
        featureRolloutTreatmentOverrides: {},
      });
    },
  }));

export const experimentsStore: StoreApi<ExperimentsState> = createExperimentsStore();
